import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { TypedMemo } from '../../../../common/types';
import { lineClock, PickLine } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type MomentLineTag = 'hook' | 'chorus' | null;

interface MomentLineRowProps {
  line: PickLine;
  isPicked: boolean;
  /** The master plays this line now. */
  isPlaying: boolean;
  tag: MomentLineTag;
  onPick: (index: number) => void;
  onPlay: (startMs: number) => void;
}

/**
 * One row of the lyric moment picker (design s7b): ▶ plays the master from the line, the synced
 * time ("—" while the lyrics sync), the text, and the "Hook" or "Chorus" tag. A click on the text
 * picks the line.
 */
const MomentLineRowComponent: React.FC<MomentLineRowProps> = ({ line, isPicked, isPlaying, tag, onPick, onPlay }) => {
  const intl = useIntl();
  const time = line.startMs === null ? null : lineClock(line.startMs);

  return (
    <div
      className={clsx(styles.pickRow, isPicked && styles.pickRowOn, isPlaying && styles.pickRowPlaying)}
      data-testid={`moment-line-${line.index}`}
    >
      <button
        type="button"
        className={styles.pickPlay}
        disabled={line.startMs === null}
        aria-label={time === null ? undefined : intl.formatMessage(fullVideoMessages.playLine, { time })}
        onClick={() => {
          if (line.startMs !== null) {
            onPlay(line.startMs);
          }
        }}
      >
        <FontAwesomeIcon icon={faPlay} />
      </button>
      <button
        type="button"
        role="checkbox"
        aria-checked={isPicked}
        className={styles.pickText}
        onClick={() => onPick(line.index)}
      >
        <span className={styles.pickTime}>{time ?? '—:—'}</span>
        <span className={styles.pickLine}>{line.text}</span>
        {tag !== null && (
          <span className={styles.pickTag}>
            {intl.formatMessage(tag === 'hook' ? fullVideoMessages.tagHook : fullVideoMessages.tagChorus)}
          </span>
        )}
      </button>
    </div>
  );
};

export const MomentLineRow = TypedMemo(MomentLineRowComponent);
